import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const STATUS_COLORS = { active: '#1a7a4c', completed: '#2563eb', archived: '#6b7280' };

const Workspace = () => {
  const { t } = useTranslation();
  const { token, isAuthenticated, isApproved, user, API } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', status: 'active' });

  useEffect(() => {
    if (isAuthenticated && isApproved) fetchProjects();
    else setLoading(false);
  }, [isAuthenticated, isApproved]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}?name=${encodeURIComponent(formData.name)}&description=${encodeURIComponent(formData.description)}&status=${formData.status}`, null, { headers });
      } else {
        await axios.post(`${API}/projects?name=${encodeURIComponent(formData.name)}&description=${encodeURIComponent(formData.description)}&status=${formData.status}`, null, { headers });
      }
      setShowForm(false);
      setEditingProject(null);
      setFormData({ name: '', description: '', status: 'active' });
      fetchProjects();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm(t('workspace.confirmDelete'))) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (selectedProject?.id === projectId) setSelectedProject(null);
      fetchProjects();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (project) => {
    setEditingProject(project);
    setFormData({ name: project.name, description: project.description, status: project.status });
    setShowForm(true);
  };

  if (!isAuthenticated || !isApproved) {
    return (
      <div data-testid="workspace-login-required" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
        <h2>{t('workspace.loginRequired')}</h2>
        <p style={{ color: 'var(--muted)' }}>{t('workspace.loginRequiredDesc')}</p>
        <a href="/auth" className="btn" style={{ marginTop: '1rem' }}>{t('auth.signIn')}</a>
      </div>
    );
  }

  if (selectedProject) {
    return <ProjectDetail project={selectedProject} onBack={() => { setSelectedProject(null); fetchProjects(); }} token={token} API={API} user={user} t={t} />;
  }

  return (
    <div data-testid="workspace-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <p className="overline">{t('workspace.overline')}</p>
          <h1 data-testid="workspace-title">{t('workspace.title')}</h1>
          <p style={{ color: 'var(--muted)' }}>{t('workspace.subtitle')}</p>
        </div>
        <button className="btn" onClick={() => { setShowForm(!showForm); setEditingProject(null); setFormData({ name: '', description: '', status: 'active' }); }} data-testid="new-project-btn">
          {showForm ? t('common.cancel') : '+ ' + t('workspace.newProject')}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card" style={{ padding: '1.5rem', marginBottom: '2rem' }} data-testid="project-form">
          <h3 style={{ marginBottom: '1rem', color: 'var(--brand)' }}>{editingProject ? t('workspace.editProject') : t('workspace.newProject')}</h3>
          <div className="form-group">
            <label>{t('workspace.projectName')} *</label>
            <input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} required data-testid="project-name-input" />
          </div>
          <div className="form-group">
            <label>{t('workspace.projectDesc')}</label>
            <textarea value={formData.description} onChange={e => setFormData(p => ({...p, description: e.target.value}))} rows="3" data-testid="project-desc-input" />
          </div>
          <div className="form-group" style={{ maxWidth: '200px' }}>
            <label>{t('workspace.projectStatus')}</label>
            <select value={formData.status} onChange={e => setFormData(p => ({...p, status: e.target.value}))} data-testid="project-status-select">
              <option value="active">{t('workspace.active')}</option>
              <option value="completed">{t('workspace.completed')}</option>
              <option value="archived">{t('workspace.archived')}</option>
            </select>
          </div>
          <button type="submit" className="btn" data-testid="project-submit-btn">{editingProject ? t('workspace.editProject') : t('workspace.createProject')}</button>
        </form>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>{t('common.loading')}</div>
      ) : projects.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
          <i className="fas fa-folder-open" style={{ fontSize: '2.5rem', color: 'var(--muted)', marginBottom: '1rem', display: 'block' }}></i>
          <p style={{ color: 'var(--muted)' }}>{t('workspace.noProjects')}</p>
        </div>
      ) : (
        <div className="grid-3">
          {projects.map(project => (
            <div key={project.id} className="card" data-testid={`project-card-${project.id}`}
              style={{ padding: '1.25rem', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }}
              onClick={() => setSelectedProject(project)}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{project.name}</h3>
                <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '9999px', background: STATUS_COLORS[project.status] + '18', color: STATUS_COLORS[project.status], fontWeight: '600' }}>
                  {t(`workspace.${project.status}`)}
                </span>
              </div>
              {project.description && <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.75rem', lineHeight: '1.5' }}>{project.description.substring(0, 120)}{project.description.length > 120 ? '...' : ''}</p>}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: 'var(--muted)' }}>
                <span><i className="fas fa-users" style={{ marginRight: '0.35rem' }}></i>{project.members?.length || 1}</span>
                <span>{t('workspace.updated')} {new Date(project.updated_at).toLocaleDateString()}</span>
              </div>
              {project.owner_id === user?.id && (
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }} onClick={e => e.stopPropagation()}>
                  <button className="btn btn-small btn-secondary" onClick={() => startEdit(project)} data-testid={`edit-project-${project.id}`}>{t('common.edit')}</button>
                  <button className="btn btn-small btn-danger" onClick={() => handleDelete(project.id)} data-testid={`delete-project-${project.id}`}>{t('common.delete')}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProjectDetail = ({ project, onBack, token, API, user, t }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [currentFolder, setCurrentFolder] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [activeTab, setActiveTab] = useState('files');
  const [projectData, setProjectData] = useState(project);
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => { fetchFiles(); fetchProject(); }, []);

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/projects/${project.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setProjectData(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchFiles = async () => {
    try {
      const params = currentFolder ? `?folder=${encodeURIComponent(currentFolder)}` : '';
      const res = await axios.get(`${API}/projects/${project.id}/files${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setFiles(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchFiles(); }, [currentFolder]);

  const handleUploadFile = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', uploadFile);
    try {
      await axios.post(`${API}/projects/${project.id}/files?folder=${encodeURIComponent(currentFolder)}`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setShowUpload(false);
      setUploadFile(null);
      fetchFiles();
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleDownloadFile = async (file) => {
    try {
      const res = await axios.get(`${API}/projects/${project.id}/files/${file.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert('Download failed'); }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await axios.delete(`${API}/projects/${project.id}/files/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchFiles();
    } catch (err) { alert('Delete failed'); }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects/${project.id}/members?email=${encodeURIComponent(newMemberEmail)}`, null, { headers: { Authorization: `Bearer ${token}` } });
      setNewMemberEmail('');
      fetchProject();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to add member'); }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await axios.delete(`${API}/projects/${project.id}/members/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchProject();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const folders = [...new Set(files.filter(f => f.folder && f.folder !== currentFolder).map(f => f.folder))];
  const isOwner = projectData.owner_id === user?.id;

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div data-testid="project-detail">
      <button className="btn btn-secondary btn-small" onClick={onBack} data-testid="back-to-projects" style={{ marginBottom: '1rem' }}>
        &larr; {t('workspace.backToProjects')}
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ marginBottom: '0.25rem' }}>{projectData.name}</h1>
          {projectData.description && <p style={{ color: 'var(--muted)' }}>{projectData.description}</p>}
          <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: '9999px', background: STATUS_COLORS[projectData.status] + '18', color: STATUS_COLORS[projectData.status], fontWeight: '600' }}>
            {t(`workspace.${projectData.status}`)}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--border)', paddingBottom: '0' }}>
        <button className={`btn btn-small ${activeTab === 'files' ? '' : 'btn-secondary'}`} onClick={() => setActiveTab('files')} data-testid="tab-files" style={{ borderRadius: '4px 4px 0 0' }}>
          <i className="fas fa-folder" style={{ marginRight: '0.4rem' }}></i>{t('workspace.files')}
        </button>
        <button className={`btn btn-small ${activeTab === 'members' ? '' : 'btn-secondary'}`} onClick={() => setActiveTab('members')} data-testid="tab-members" style={{ borderRadius: '4px 4px 0 0' }}>
          <i className="fas fa-users" style={{ marginRight: '0.4rem' }}></i>{t('workspace.members')} ({projectData.members?.length || 0})
        </button>
      </div>

      {activeTab === 'files' && (
        <div data-testid="files-tab">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
              <button className="btn btn-small btn-secondary" onClick={() => setCurrentFolder('')} style={{ padding: '0.2rem 0.5rem' }}>
                <i className="fas fa-home"></i>
              </button>
              {currentFolder && (
                <span style={{ color: 'var(--muted)' }}>/ {currentFolder}</span>
              )}
            </div>
            <button className="btn btn-small" onClick={() => setShowUpload(!showUpload)} data-testid="upload-file-btn">
              {showUpload ? t('common.cancel') : '+ ' + t('workspace.uploadFile')}
            </button>
          </div>

          {showUpload && (
            <form onSubmit={handleUploadFile} className="card" style={{ padding: '1rem', marginBottom: '1rem', display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }} data-testid="file-upload-form">
              <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: '200px' }}>
                <label>{t('cms.file')}</label>
                <input type="file" onChange={e => setUploadFile(e.target.files[0])} data-testid="project-file-input" />
              </div>
              <button type="submit" className="btn btn-small" disabled={uploading} data-testid="project-file-submit">
                {uploading ? t('cms.uploading') : t('cms.uploadBtn')}
              </button>
            </form>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>{t('common.loading')}</div>
          ) : files.length === 0 ? (
            <div className="card" style={{ padding: '2.5rem', textAlign: 'center' }}>
              <i className="fas fa-folder-open" style={{ fontSize: '2rem', color: 'var(--muted)', marginBottom: '0.75rem', display: 'block' }}></i>
              <p style={{ color: 'var(--muted)' }}>{t('workspace.noFiles')}</p>
            </div>
          ) : (
            <div>
              {files.map(file => (
                <div key={file.id} className="card" data-testid={`file-${file.id}`} style={{ padding: '0.75rem 1.25rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <i className="fas fa-file" style={{ color: 'var(--brand)', fontSize: '1.2rem' }}></i>
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '0.9rem' }}>{file.original_filename}</strong>
                    <p style={{ color: 'var(--muted)', fontSize: '0.75rem', margin: 0 }}>
                      {formatSize(file.size)} &middot; {file.uploader_name} &middot; {new Date(file.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button className="btn btn-small" onClick={() => handleDownloadFile(file)} data-testid={`download-file-${file.id}`}>{t('common.download')}</button>
                    {(file.uploaded_by === user?.id || isOwner) && (
                      <button className="btn btn-small btn-danger" onClick={() => handleDeleteFile(file.id)} data-testid={`delete-file-${file.id}`}>{t('common.delete')}</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'members' && (
        <div data-testid="members-tab">
          {isOwner && (
            <form onSubmit={handleAddMember} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }} data-testid="add-member-form">
              <input placeholder={t('workspace.memberEmail')} value={newMemberEmail} onChange={e => setNewMemberEmail(e.target.value)} required type="email" style={{ flex: 1, maxWidth: '350px' }} data-testid="member-email-input" />
              <button type="submit" className="btn btn-small" data-testid="add-member-btn">{t('workspace.addMember')}</button>
            </form>
          )}
          <div>
            {(projectData.members || []).map(member => (
              <div key={member.id} className="card" data-testid={`member-${member.id}`} style={{ padding: '0.75rem 1.25rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--brand)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.85rem' }}>
                    {member.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <strong style={{ fontSize: '0.9rem' }}>{member.name}</strong>
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: '9999px', background: member.role === 'owner' ? 'var(--brand)' : 'var(--border)', color: member.role === 'owner' ? '#fff' : 'var(--muted)' }}>
                      {t(`workspace.${member.role}`)}
                    </span>
                  </div>
                </div>
                {isOwner && member.role !== 'owner' && (
                  <button className="btn btn-small btn-danger" onClick={() => handleRemoveMember(member.id)} data-testid={`remove-member-${member.id}`}>{t('workspace.removeMember')}</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Workspace;
